import React, { useState, useCallback } from "react";
import MUITreeView from "@mui/lab/TreeView";
import TreeDocumentItem from "./tree-document-item";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CloudUpload } from "lucide-react";
import { Button } from "@/components/ui/button";
import TreeFolderItem from "./tree-folder-item";
import ToolbarMenu from "@/components/toolbarMenu";
import useUploadPersonalDocuments from "@/hooks/personalDocuments/useUploadPersonalDocuments";
import useMovePersonalDocument from "@/hooks/personalDocuments/useMovePersonalDocument";
import useMovePersonalFolder from "@/hooks/personalDocuments/useMovePersonalFolder";
import i18n from "@/i18n";
import { Trans } from "react-i18next";
import Breadcrumbs from "@/components/breadcrumbs";

const { t } = i18n;

const TreeView = ({ documents, folderIds }) => {
  const rootNodeId = "personal-documents.my-documents-";
  const [expanded, setExpanded] = useState([rootNodeId]);
  const [selected, setSelected] = useState(null);
  const [selectedPath, setSelectedPath] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const rootIsExpanded = expanded.some((e) => e === rootNodeId);
  const uploadMutation = useUploadPersonalDocuments();
  const moveDocumentMutation = useMovePersonalDocument();
  const moveFolderMutation = useMovePersonalFolder();

  const handleToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  const handleExpandClick = () => {
    setExpanded((oldExpanded) =>
      oldExpanded.length && rootIsExpanded ? [] : folderIds.toArray(),
    );
  };

  // Helper function to get all parent folder nodeIds for a given path
  const getParentFolderNodeIds = useCallback(
    (folderPath) => {
      const nodeIds = [rootNodeId]; // Always include root

      if (!folderPath) return nodeIds;

      const pathParts = folderPath.split("/").filter((part) => part !== "");
      let currentPath = "";

      for (const part of pathParts) {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        const nodeId = `${part}-${currentPath}`;
        nodeIds.push(nodeId);
      }

      return nodeIds;
    },
    [rootNodeId],
  );

  // Helper function to expand folder path when folder is created
  const expandFolderPath = useCallback(
    (folderPath) => {
      const folderNodeIds = getParentFolderNodeIds(folderPath);
      setExpanded((oldExpanded) => {
        const newExpanded = [...oldExpanded];
        folderNodeIds.forEach((nodeId) => {
          if (!newExpanded.includes(nodeId)) {
            newExpanded.push(nodeId);
          }
        });
        return newExpanded;
      });
    },
    [getParentFolderNodeIds],
  );

  const handleDrop = useCallback(
    (prefix, documents) => {
      setUploadProgress(0);

      // Expand the target folder and all its parents
      const folderNodeIds = getParentFolderNodeIds(prefix);
      setExpanded((oldExpanded) => {
        const newExpanded = [...oldExpanded];
        folderNodeIds.forEach((nodeId) => {
          if (!newExpanded.includes(nodeId)) {
            newExpanded.push(nodeId);
          }
        });
        return newExpanded;
      });

      uploadMutation.mutate(
        {
          prefix,
          documents,
          onUploadProgress: (progress) => {
            setUploadProgress(progress);
          },
        },
        {
          onSuccess: () => {
            setUploadProgress(null);
          },
          onError: () => {
            setUploadProgress(null);
          },
        },
      );
    },
    [uploadMutation, getParentFolderNodeIds],
  );

  // Helper function to find a node by its nodeId
  const findNodeByNodeId = useCallback((node, targetNodeId) => {
    const absolutePath = node.get("absolutePath");
    const name = node.get("name");
    const nodeId = `${name}-${absolutePath}`;

    if (nodeId === targetNodeId) {
      return node;
    }

    const children = node.get("children");
    if (children && children.size > 0) {
      for (const child of children) {
        const found = findNodeByNodeId(child, targetNodeId);
        if (found) return found;
      }
    }

    return null;
  }, []);

  // Helper function to get parent path for files
  const getParentPath = useCallback((filePath) => {
    const parts = filePath.split("/");
    return parts.slice(0, -1).join("/") || "";
  }, []);

  // Handle node selection
  const handleSelect = useCallback(
    (event, nodeId) => {
      setSelected(nodeId);

      if (nodeId) {
        const selectedNode = findNodeByNodeId(documents, nodeId);
        if (selectedNode) {
          const path = selectedNode.get("absolutePath");
          const isFolder = selectedNode.get("isFolder");
          // If it's a file, use parent folder path
          const targetPath = isFolder ? path : getParentPath(path);
          setSelectedPath(targetPath);
        }
      } else {
        setSelectedPath("");
      }
    },
    [documents, findNodeByNodeId, getParentPath],
  );

  // Helper to get selected folder name for UI display
  const getSelectedFolderName = useCallback(() => {
    const rootFolderName = t("menu.mainMenu.user.personal_documents");
    if (!selected || !selectedPath) return rootFolderName;

    const selectedNode = findNodeByNodeId(documents, selected);
    if (selectedNode) {
      const isFolder = selectedNode.get("isFolder");
      if (isFolder) {
        return selectedNode.get("name");
      } else {
        // For files, return parent folder name
        const parts = selectedPath.split("/");
        return parts[parts.length - 1] || rootFolderName;
      }
    }
    return rootFolderName;
  }, [selected, selectedPath, documents, findNodeByNodeId]);

  // Handle upload button click
  const handleUploadClick = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        // Use selected path or root path as fallback
        const uploadPath = selectedPath || "";
        handleDrop(uploadPath, files);
      }
    };
    input.click();
  }, [selectedPath, handleDrop]);

  const renderNode = (root) => {
    const children = root.get("children");
    const absolutePath = root.get("absolutePath");
    const name = root.get("name");
    const isFolder = root.get("isFolder");
    const nodeId = `${name}-${absolutePath}`;

    return isFolder ? (
      <TreeFolderItem
        key={nodeId}
        name={name}
        absolutePath={absolutePath}
        nodeId={nodeId}
        isRoot={root.get("isRoot")}
        expandFolderPath={expandFolderPath}
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.setData(
            "drop-data",
            JSON.stringify({ absolutePath, type: "folder" }),
          );
        }}
        handleDocumentDrop={(e) => {
          const dropData = JSON.parse(e.dataTransfer.getData("drop-data"));
          return moveDocumentMutation.mutate({
            from: dropData.absolutePath,
            to: absolutePath,
          });
        }}
        handleFolderDrop={(e) => {
          const dropData = JSON.parse(e.dataTransfer.getData("drop-data"));
          return moveFolderMutation.mutate({
            from: dropData.absolutePath,
            to: absolutePath,
          });
        }}
        handleBinaryFileDrop={(documents) =>
          handleDrop(absolutePath, documents)
        }
      >
        {!!children.size && children.map((c) => renderNode(c))}
      </TreeFolderItem>
    ) : (
      <TreeDocumentItem
        key={nodeId}
        name={name}
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.setData(
            "drop-data",
            JSON.stringify({ absolutePath, type: "file" }),
          );
        }}
        absolutePath={absolutePath}
        nodeId={nodeId}
        handleDrop={(documents) => handleDrop(absolutePath, documents)}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex h-[50px] items-center justify-between">
        <Breadcrumbs items={[{ label: t("personal-documents.main-header") }]} />
        <ToolbarMenu>
          <Button
            onClick={handleUploadClick}
            disabled={uploadMutation.isPending}
          >
            <CloudUpload />
            {uploadMutation.isPending && uploadProgress !== null ? (
              <span>
                {t("generic.uploading")} {uploadProgress}%
              </span>
            ) : (
              <Trans
                i18nKey="personal-documents.upload-to-folder"
                values={{ folderName: getSelectedFolderName() }}
              />
            )}
          </Button>
          <Button onClick={handleExpandClick} variant="outline">
            {t(
              expanded.length && rootIsExpanded
                ? "personal-documents.collapse-all-folders"
                : "personal-documents.expand-all-folders",
            )}
          </Button>
        </ToolbarMenu>
      </div>
      <div className="rounded-lg border border-edge bg-background p-4 shadow">
        <MUITreeView
          aria-label="file system navigator"
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          defaultExpanded={[rootNodeId]}
          expanded={expanded}
          selected={selected}
          onNodeToggle={handleToggle}
          onNodeSelect={handleSelect}
        >
          {renderNode(documents)}
        </MUITreeView>
      </div>
    </div>
  );
};
export default TreeView;
